import React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { styled } from '@mui/material/styles';

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  '& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)': {
    transform: 'translate(14px, 16px) scale(1)'
  },
  '& .MuiOutlinedInput-root': {
    padding: '4px'
  },
  '& .MuiAutocomplete-option': {
    fontSize: '14px',
    '& > span': {
      marginRight: 10,
      fontSize: '18px'
    }
  }
}));

const AutoCompleteField = React.forwardRef(({
  options,
  value,
  onChange,
  label,
  getOptionLabel,
  disabled,
  readOnly,
  error,
  helperText,
  required,
  searchableFields = ['nome', 'label'], // Default searchable field is 'label'
  noResultsText = 'Nenhum resultado encontrado',
  noResultsAction = null, // { text: 'Criar novo', action: () => {} }
  className = '', // Adicionar suporte para className customizado
}, ref) => {
  // Convert string props to booleans
  const isDisabled = disabled === 'true' || disabled === true;
  const isReadOnly = readOnly === 'true' || readOnly === true;
  
  // State to track input value
  const [inputValue, setInputValue] = React.useState('');
  
  const filterOptions = (options, state) => {
    const query = state.inputValue.toLowerCase();

    const filtered = query.length > 0 ? options.filter((option) => {
      return searchableFields.some(field => {
        // If the field exists on the option object, we check if it matches the query
        if (option[field]) {
          return option[field].toString().toLowerCase().includes(query);
        }
        return false;
      });
    }) : options;

    // If there are no results and we have a noResultsAction, add it to the options
    if (filtered.length === 0 && noResultsAction && query.length > 0) {
      return [{
        __isNoResultsAction: true,
        label: noResultsAction.text,
        action: noResultsAction.action
      }];
    }

    return filtered;
  };

  // Handler for input changes
  const handleInputChange = (event, value, reason) => {
    if (reason === 'input') {
      setInputValue(value);
    }
  };

  // Handler for option selection
  const handleChange = (event, selectedOption) => {
    if (selectedOption && selectedOption.__isNoResultsAction) {
      // Call the action function with the current input value
      selectedOption.action(inputValue);
    } else if (onChange) {
      onChange(event, selectedOption);
    }
  };

  // Handler for Enter key when only one option is available
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !isReadOnly) {
      // Get the current filtered options based on the input value
      const inputValue = event.target.value.toLowerCase();
      const filtered = inputValue.length > 0 ? options.filter((option) => {
        return searchableFields.some(field => {
          if (option[field]) {
            return option[field].toString().toLowerCase().includes(inputValue);
          }
          return false;
        });
      }) : options;
      
      if (filtered.length === 1) {
        event.preventDefault();
        handleChange(event, filtered[0]);
      }
    }
  };

  return (
    <StyledAutocomplete
      options={options}
      value={value && value !== "" ? value : null}
      onChange={isReadOnly ? undefined : handleChange}
      onInputChange={handleInputChange}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, value) => {
        // Handle null/undefined values
        if (!option && !value) return true;
        if (!option || !value) return false;
        
        // Handle empty string values
        if (value === "" || value === null) return false;
        
        // Handle different option types
        if (option.idcustomer && value.idcustomer) return option.idcustomer === value.idcustomer;
        if (option.idproduct && value.idproduct) return option.idproduct === value.idproduct;
        if (option.posto && value.posto) return option.posto === value.posto;
        if (option.value && value.value) return option.value === value.value;
        
        // Handle string comparison for simple values
        if (typeof option === 'string' && typeof value === 'string') return option === value;
        
        // Fallback to string comparison
        return JSON.stringify(option) === JSON.stringify(value);
      }}
      filterOptions={isReadOnly ? () => [] : filterOptions}
      noOptionsText={noResultsText}
      renderOption={(props, option) => {
        if (option.__isNoResultsAction) {
          return (
            <li {...props} style={{ 
              ...props.style, 
              fontStyle: 'italic', 
              color: '#1976d2',
              cursor: 'pointer'
            }}>
              {option.label}
            </li>
          );
        }
        return <li {...props}>{getOptionLabel(option)}</li>;
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={ref}
          label={label}
          variant="outlined"
          fullWidth
          error={error}
          helperText={helperText}
          required={required}
          onKeyDown={handleKeyDown}
          InputProps={{
            ...params.InputProps,
            readOnly: isReadOnly,
          }}
        />
      )}
      disabled={isDisabled || isReadOnly}
      autoComplete
      openOnFocus={!isReadOnly}
      className={className}
      ListboxProps={{
        style: {
          maxHeight: '300px',
          overflow: 'auto'
        }
      }}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'flip',
              enabled: true,
            },
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                altAxis: true,
                altBoundary: true,
                tether: true,
                rootBoundary: 'viewport',
                padding: 8,
              },
            },
          ],
          className: className ? `${className}-popper` : undefined,
        },
      }}
      getOptionKey={(option) => {
        if (option?.idcustomer) return `customer-${option.idcustomer}`;
        if (option?.idproduct) return `product-${option.idproduct}`;
        if (option?.posto) return `posto-${option.posto}`;
        if (option?.value) return `payment-${option.value}`;
        return JSON.stringify(option);
      }}
    />
  );
});

AutoCompleteField.displayName = 'AutoCompleteField';

export default AutoCompleteField;
